'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconBack16, IconBack24 } from '@cineamo/ui/src/icons/ic-back';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import PageLeaveWarningModal from '@cineamo/composables/src/ticket-booking/PageLeaveWarningModal';

const UNSAVED_CHANGES_KEY = 'isPageHaveUnsavedChanges';

function goBack() {
    // Back to wherever the guest was, like the browser's back button. `document.referrer` is only the page
    // that loaded this document, often trimmed to the origin, so it led to the start page. Embedded in an
    // iframe, history would step the host page, so the referrer stays the way back there.
    if (window.self === window.top && window.history.length > 1) {
        window.history.back();
        return;
    }

    // A tab opened on the booking page: the page that opened it.
    if (document.referrer && document.referrer !== window.location.href) {
        window.location.href = document.referrer;
        return;
    }

    // Last fallback: go to home
    window.location.href = '/';
}

/**
 * Back to the page the guest came from. `iconOnly` is the arrow alone, for the header on small screens. `onBack`
 * goes first and returns whether it handled the step back (the booking's checkout steps), else the page is left.
 */
const BackButton: React.FC<{ label?: string; iconOnly?: boolean; className?: string; onBack?: () => boolean }> = ({
    label,
    iconOnly,
    className,
    onBack
}) => {
    const t = useTranslations();

    const { track } = useAnalytics();
    const [isLeaveWarningOpen, setIsLeaveWarningOpen] = useState(false);

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onBack?.()) return;
        // Seats are held or being paid for: ask first, like a link out of the checkout does.
        if (sessionStorage.getItem(UNSAVED_CHANGES_KEY) === 'true') {
            setIsLeaveWarningOpen(true);
            return;
        }
        goBack();
    };

    const leave = () => {
        setIsLeaveWarningOpen(false);
        track('Checkout Abandoned', { nextRoute: 'back' });
        // Confirmed once: the page's own beforeunload prompt must not ask again.
        sessionStorage.setItem(UNSAVED_CHANGES_KEY, 'false');
        goBack();
    };

    const stay = () => {
        setIsLeaveWarningOpen(false);
        track('Checkout Abandon Canceled', { nextRoute: 'back' });
    };

    return (
        <>
            {isLeaveWarningOpen && (
                <PageLeaveWarningModal
                    modalIsOpen={isLeaveWarningOpen}
                    setModalIsOpen={setIsLeaveWarningOpen}
                    onClose={stay}
                    handleConfirm={leave}
                    handleCancel={stay}
                />
            )}
            <div
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleClick(e);
                    }
                }}
                className={cn(
                    'flex items-center gap-8 text-body-10 hover:opacity-80 transition-opacity cursor-pointer',
                    iconOnly &&
                        'p-4 rounded-4 outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]',
                    className
                )}
                aria-label={label || t('common.back-to-program')}>
                {iconOnly ? (
                    <IconBack24 fill="currentColor" />
                ) : (
                    <>
                        <IconBack16 />
                        <span>{label || t('common.back-to-program')}</span>
                    </>
                )}
            </div>
        </>
    );
};

export default BackButton;
